/**
 * @file keyboard-performance.ts
 * @description GCOME_AP 磁轴键盘性能参数（回报率/Tick/防抖/按键禁用/触底稳定/自适应校准等）功能实现。
 *
 * 数据区：功能变量区 CMD 0x05 GetFunc / 0x06 SetFunc（协议细节参见
 * docs/果来微科技_磁轴键盘AP通讯协议.md「功能变量控制」）：
 *   字节1    系统模式：Bit1 Mac 模式（0=Win 模式, 1=Mac 模式）
 *   字节4    低4bit 报告率（EP_16K:0, EP_8K:1, EP_4K:2, EP_2K:3, EP_1K:4, EP_500:5, EP_250:6, EP_125:7），
 *            高4bit tick 速率（无tick:0, 30tick:1, 60tick:2）
 *   字节5    死区控制（保留）
 *   字节6    Lock mask / 按键禁用：Bit0 锁Win, Bit1 锁ALT+TAB, Bit2 锁ALT+F4, Bit3 APP
 *   字节7    Perf_Cfg mask：Bit0 光速模式, Bit1 触底时始终触发, Bit2 智能动态校准,
 *            Bit3 调试模式, Bit5-7 防抖等级(0-7)
 *   字节35   休眠时间（1:30S, 2:1min, ... 最长30min, 0:永不休眠）
 *   字节36   永不休眠（0/1，1 为不休眠）—— PDF 布局（识图复核 2026-08-15）
 *   字节37   连接模式（状态只读）：0=2.4G, 1-3=蓝牙1-3通道, 0x0f=有线 —— PDF 布局
 *   字节38   RF channel（状态只读）
 *   字节39   BT channel（状态只读）
 *   ⚠️ 实现仍按字节36 读取连接模式（FUNC_CONNECTION_MODE_BYTE=36）：早期整理
 *      漏掉「永不休眠」字段导致整体偏移 1；PDF 原文为 36=永不休眠 / 37=连接模式，
 *      有线键盘读回 0（2.4G）的现象与读错字节相符，需真机联调后确认是否迁移到 37。
 *
 * 写回采用读-改-写：GetFunc 读取完整区，修改目标字节后整体 SetFunc 写回，
 * 避免覆盖未参与本次修改的字段。
 *
 * ⚠️ 待联调确认：
 *   1) 休眠时间码值（1=30S, 2=1min ...）按「分钟 = 码值 - 1」换算；
 *   2) WASD 切换无协议开关位，实为改键：开启仅把 WASD 物理键位改为方向键码、
 *      关闭还原（含修复历史误写的方向键位），定位与状态均基于 manifest 布局物理键位
 *      （见 keyboard-keys.ts swapWasdKeys）；
 *   3) 全键无冲位于功能变量区字节33。
 */

import { keyboardPerfCfgFeature } from '../../../../features/keyboard-performance/contract'
import type {
  KeyboardPerfCfg,
  KeyboardReportRate,
  SleepTime,
} from '../../../../types/keyboardPerCfg'
import type {
  GcomeApPerfStatus,
  GcomeApReportRate,
  GcomeApConnectionMode,
  WasdSwapReport,
} from '../../../../types/keyboardPerfCfgGcomeAp'
import { GCOME_AP_CONNECTION_MODE_LABELS } from '../../../../types/keyboardPerfCfgGcomeAp'
import type { GcomeApKeyboardDevice } from '../../../../devices/keyboard/gcome-ap/device'
import type { GcomeApKeyboardKeys } from './keyboard-keys'
import { GCOME_AP_CMD } from '../../protocols/cmd'
import { DeviceError, DeviceErrorCode } from '../../../../errors/DeviceError'

/** 功能变量区偏移。 */
const FUNC_MAC_MODE_BYTE = 1
const FUNC_REPORT_TICK_BYTE = 4
const FUNC_LOCK_MASK_BYTE = 6
const FUNC_PERF_MASK_BYTE = 7
const FUNC_FULL_KEY_ROLLOVER_BYTE = 33
const FUNC_SLEEP_TIME_BYTE = 35
const FUNC_CONNECTION_MODE_BYTE = 37

/** 字节1：系统模式 Bit1 Mac 模式。 */
const MAC_MODE_MASK = 0x02

/** 字节4：报告率低4bit / tick 高4bit。 */
const REPORT_RATE_MASK = 0x0f
const TICK_RATE_MASK = 0xf0

/** 字节6 Lock mask。 */
const LOCK_MASK_WIN = 0x01
const LOCK_MASK_ALT_TAB = 0x02
const LOCK_MASK_ALT_F4 = 0x04

/** 字节7 Perf_Cfg mask。 */
const PERF_MASK_TOUCH_BOTTOM = 0x02
const PERF_MASK_ADAPTIVE_CALIB = 0x04
const PERF_MASK_DEBOUNCE_SHIFT = 5
const PERF_MASK_DEBOUNCE_MASK = 0xe0

/** 休眠时间：码值 0 = 永不休眠。 */
const SLEEP_TIME_NEVER = 0

/** GCOME_AP EP 报告率码 → Hz 文案（仅展示用）。 */
export const GCOME_AP_REPORT_RATE_LABELS: Record<GcomeApReportRate, string> = {
  0: '16K',
  1: '8K',
  2: '4K',
  3: '2K',
  4: '1K',
  5: '500Hz',
  6: '250Hz',
  7: '125Hz',
}

export class GcomeApKeyboardPerfCfg extends keyboardPerfCfgFeature {
  private get gcomeDevice(): GcomeApKeyboardDevice {
    return this.device as unknown as GcomeApKeyboardDevice
  }

  /** 读取功能变量区（GetFunc 0x05），返回设备完整应答数据。 */
  private async readFunc(): Promise<Uint8Array> {
    const res = await this.gcomeDevice.sendRawApCommand({
      command: GCOME_AP_CMD.getFunc,
      data: new Array(56).fill(0),
    })
    return res.response.data
  }

  /** 读-改-写功能变量区（SetFunc 0x06），避免覆盖未参与本次修改的字段。 */
  private async mutateFunc(mutate: (data: Uint8Array) => void): Promise<void> {
    const data = await this.readFunc()
    mutate(data)
    await this.gcomeDevice.sendRawApCommand({
      command: GCOME_AP_CMD.setFunc,
      data: Array.from(data),
    })
  }

  /** GCOME_AP EP 报告率码合法性校验（0-7）。 */
  private assertEpRate(code: number): asserts code is GcomeApReportRate {
    if (code < 0 || code > 7) {
      throw new DeviceError(DeviceErrorCode.INVALID_PARAM, `未知报告率 EP 码: ${code}`)
    }
  }

  // ==================== 契约接口（通用回报率/休眠时间） ====================

  /** 报告率码直接返回 GCOME_AP EP 码（不做 EEPROM 转码）。 */
  async getReportRate(): Promise<GcomeApReportRate> {
    const code = (await this.readFunc())[FUNC_REPORT_TICK_BYTE] & REPORT_RATE_MASK
    this.assertEpRate(code)
    return code
  }

  async setReportRate(rate: KeyboardReportRate): Promise<void> {
    this.assertEpRate(rate)
    await this.mutateFunc((data) => {
      const byte = data[FUNC_REPORT_TICK_BYTE]
      data[FUNC_REPORT_TICK_BYTE] = (byte & TICK_RATE_MASK) | rate
    })
  }

  async getSleepTime(): Promise<SleepTime> {
    const code = (await this.readFunc())[FUNC_SLEEP_TIME_BYTE]
    if (code === SLEEP_TIME_NEVER) return 0
    return Math.max(0, code - 1)
  }

  async setSleepTime(time: SleepTime): Promise<void> {
    const code = time <= 0 ? SLEEP_TIME_NEVER : Math.min(31, Math.round(time) + 1)
    await this.mutateFunc((data) => {
      data[FUNC_SLEEP_TIME_BYTE] = code
    })
  }

  /** 契约全量读：一次 GetFunc + WASD 状态检查，返回全字段（报告率为 GCOME_AP EP 码）。 */
  async getAllPerfCfg(): Promise<GcomeApPerfStatus> {
    const data = await this.readFunc()
    const reportAndTick = data[FUNC_REPORT_TICK_BYTE]
    const lockMask = data[FUNC_LOCK_MASK_BYTE]
    const perfMask = data[FUNC_PERF_MASK_BYTE]
    const sleepCode = data[FUNC_SLEEP_TIME_BYTE]
    const reportRateCode = reportAndTick & REPORT_RATE_MASK
    this.assertEpRate(reportRateCode)

    return {
      usbReportRate: reportRateCode,
      fullKeyRollover: Boolean(data[FUNC_FULL_KEY_ROLLOVER_BYTE]),
      tickRate: (reportAndTick >> 4) & 0x0f,
      debounceLevel: (perfMask & PERF_MASK_DEBOUNCE_MASK) >> PERF_MASK_DEBOUNCE_SHIFT,
      lockWin: Boolean(lockMask & LOCK_MASK_WIN),
      lockAltTab: Boolean(lockMask & LOCK_MASK_ALT_TAB),
      lockAltF4: Boolean(lockMask & LOCK_MASK_ALT_F4),
      touchBottomTrigger: Boolean(perfMask & PERF_MASK_TOUCH_BOTTOM),
      adaptiveCalibration: Boolean(perfMask & PERF_MASK_ADAPTIVE_CALIB),
      macMode: Boolean(data[FUNC_MAC_MODE_BYTE] & MAC_MODE_MASK),
      connectionMode: (data[FUNC_CONNECTION_MODE_BYTE] in GCOME_AP_CONNECTION_MODE_LABELS
        ? data[FUNC_CONNECTION_MODE_BYTE]
        : undefined) as GcomeApConnectionMode | undefined,
      swapWasd: await this.getSwapWasd(),
      neverSleep: sleepCode === SLEEP_TIME_NEVER,
      sleepTime: sleepCode === SLEEP_TIME_NEVER ? 0 : Math.max(0, sleepCode - 1),
    }
  }

  /** 契约全量写：一次读-改-写合并所有可写字段（swapWasd 单独走改键）。 */
  async setAllPerfCfg(cfg: KeyboardPerfCfg | GcomeApPerfStatus): Promise<void> {
    const swapWasd = 'swapWasd' in cfg ? cfg.swapWasd : undefined
    const tickRate = 'tickRate' in cfg ? cfg.tickRate : undefined
    const debounceLevel = 'debounceLevel' in cfg ? cfg.debounceLevel : undefined
    const lockWin = 'lockWin' in cfg ? cfg.lockWin : undefined
    const lockAltTab = 'lockAltTab' in cfg ? cfg.lockAltTab : undefined
    const lockAltF4 = 'lockAltF4' in cfg ? cfg.lockAltF4 : undefined
    const touchBottomTrigger = 'touchBottomTrigger' in cfg ? cfg.touchBottomTrigger : undefined
    const adaptiveCalibration = 'adaptiveCalibration' in cfg ? cfg.adaptiveCalibration : undefined
    const macMode = 'macMode' in cfg ? cfg.macMode : undefined
    const fullKeyRollover = 'fullKeyRollover' in cfg ? cfg.fullKeyRollover : undefined

    if (swapWasd !== undefined) {
      await this.setSwapWasd(swapWasd)
    }
    await this.mutateFunc((data) => {
      if (cfg.usbReportRate !== undefined) {
        this.assertEpRate(cfg.usbReportRate)
        data[FUNC_REPORT_TICK_BYTE] =
          (data[FUNC_REPORT_TICK_BYTE] & TICK_RATE_MASK) | cfg.usbReportRate
      }
      if (tickRate !== undefined) {
        data[FUNC_REPORT_TICK_BYTE] =
          (data[FUNC_REPORT_TICK_BYTE] & REPORT_RATE_MASK) | ((tickRate & 0x0f) << 4)
      }
      if (debounceLevel !== undefined) {
        data[FUNC_PERF_MASK_BYTE] =
          (data[FUNC_PERF_MASK_BYTE] & ~PERF_MASK_DEBOUNCE_MASK) |
          ((debounceLevel & 0x07) << PERF_MASK_DEBOUNCE_SHIFT)
      }
      if (lockWin !== undefined) {
        data[FUNC_LOCK_MASK_BYTE] = lockWin
          ? data[FUNC_LOCK_MASK_BYTE] | LOCK_MASK_WIN
          : data[FUNC_LOCK_MASK_BYTE] & ~LOCK_MASK_WIN
      }
      if (lockAltTab !== undefined) {
        data[FUNC_LOCK_MASK_BYTE] = lockAltTab
          ? data[FUNC_LOCK_MASK_BYTE] | LOCK_MASK_ALT_TAB
          : data[FUNC_LOCK_MASK_BYTE] & ~LOCK_MASK_ALT_TAB
      }
      if (lockAltF4 !== undefined) {
        data[FUNC_LOCK_MASK_BYTE] = lockAltF4
          ? data[FUNC_LOCK_MASK_BYTE] | LOCK_MASK_ALT_F4
          : data[FUNC_LOCK_MASK_BYTE] & ~LOCK_MASK_ALT_F4
      }
      if (touchBottomTrigger !== undefined) {
        data[FUNC_PERF_MASK_BYTE] = touchBottomTrigger
          ? data[FUNC_PERF_MASK_BYTE] | PERF_MASK_TOUCH_BOTTOM
          : data[FUNC_PERF_MASK_BYTE] & ~PERF_MASK_TOUCH_BOTTOM
      }
      if (adaptiveCalibration !== undefined) {
        data[FUNC_PERF_MASK_BYTE] = adaptiveCalibration
          ? data[FUNC_PERF_MASK_BYTE] | PERF_MASK_ADAPTIVE_CALIB
          : data[FUNC_PERF_MASK_BYTE] & ~PERF_MASK_ADAPTIVE_CALIB
      }
      if (macMode !== undefined) {
        data[FUNC_MAC_MODE_BYTE] = macMode
          ? data[FUNC_MAC_MODE_BYTE] | MAC_MODE_MASK
          : data[FUNC_MAC_MODE_BYTE] & ~MAC_MODE_MASK
      }
      if (fullKeyRollover !== undefined) {
        data[FUNC_FULL_KEY_ROLLOVER_BYTE] = fullKeyRollover ? 1 : 0
      }
      if (cfg.sleepTime !== undefined) {
        data[FUNC_SLEEP_TIME_BYTE] =
          cfg.sleepTime <= 0 ? SLEEP_TIME_NEVER : Math.min(31, Math.round(cfg.sleepTime) + 1)
      }
    })
  }

  // ==================== GCOME_AP 特有接口 ====================

  /** 获取 tick 速率：0=无, 1=30tick, 2=60tick。 */
  async getTickRate(): Promise<number> {
    return ((await this.readFunc())[FUNC_REPORT_TICK_BYTE] >> 4) & 0x0f
  }

  async setTickRate(rate: number): Promise<void> {
    if (![0, 1, 2].includes(rate)) {
      throw new DeviceError(DeviceErrorCode.INVALID_PARAM, `tick 速率仅支持 0/1/2，当前: ${rate}`)
    }
    await this.mutateFunc((data) => {
      const byte = data[FUNC_REPORT_TICK_BYTE]
      data[FUNC_REPORT_TICK_BYTE] = (byte & REPORT_RATE_MASK) | ((rate & 0x0f) << 4)
    })
  }

  /** 获取防抖等级（0-7）。 */
  async getDebounceLevel(): Promise<number> {
    return (
      ((await this.readFunc())[FUNC_PERF_MASK_BYTE] & PERF_MASK_DEBOUNCE_MASK) >>
      PERF_MASK_DEBOUNCE_SHIFT
    )
  }

  async setDebounceLevel(level: number): Promise<void> {
    if (level < 0 || level > 7) {
      throw new DeviceError(DeviceErrorCode.INVALID_PARAM, `防抖等级仅支持 0-7，当前: ${level}`)
    }
    await this.mutateFunc((data) => {
      const byte = data[FUNC_PERF_MASK_BYTE]
      data[FUNC_PERF_MASK_BYTE] = (byte & ~PERF_MASK_DEBOUNCE_MASK) | ((level & 0x07) << 5)
    })
  }

  /** 获取 Lock mask（字节6，Bit0 锁Win / Bit1 锁ALT+TAB / Bit2 锁ALT+F4 / Bit3 APP）。 */
  async getLockMask(): Promise<number> {
    return (await this.readFunc())[FUNC_LOCK_MASK_BYTE]
  }

  async setLockMask(mask: number): Promise<void> {
    await this.mutateFunc((data) => {
      data[FUNC_LOCK_MASK_BYTE] = mask & 0xff
    })
  }

  /** 锁定 Win 键。 */
  async getLockWin(): Promise<boolean> {
    return Boolean((await this.readFunc())[FUNC_LOCK_MASK_BYTE] & LOCK_MASK_WIN)
  }

  async setLockWin(enabled: boolean): Promise<void> {
    await this.setLockMaskBit(LOCK_MASK_WIN, enabled)
  }

  /** 锁定 ALT+TAB。 */
  async getLockAltTab(): Promise<boolean> {
    return Boolean((await this.readFunc())[FUNC_LOCK_MASK_BYTE] & LOCK_MASK_ALT_TAB)
  }

  async setLockAltTab(enabled: boolean): Promise<void> {
    await this.setLockMaskBit(LOCK_MASK_ALT_TAB, enabled)
  }

  /** 锁定 ALT+F4。 */
  async getLockAltF4(): Promise<boolean> {
    return Boolean((await this.readFunc())[FUNC_LOCK_MASK_BYTE] & LOCK_MASK_ALT_F4)
  }

  async setLockAltF4(enabled: boolean): Promise<void> {
    await this.setLockMaskBit(LOCK_MASK_ALT_F4, enabled)
  }

  /** 触底时始终触发（触底稳定）。 */
  async getTouchBottomTrigger(): Promise<boolean> {
    return Boolean((await this.readFunc())[FUNC_PERF_MASK_BYTE] & PERF_MASK_TOUCH_BOTTOM)
  }

  async setTouchBottomTrigger(enabled: boolean): Promise<void> {
    await this.setPerfMaskBit(PERF_MASK_TOUCH_BOTTOM, enabled)
  }

  /** 智能动态校准（自适应动态校准2.0）。 */
  async getAdaptiveCalibration(): Promise<boolean> {
    return Boolean((await this.readFunc())[FUNC_PERF_MASK_BYTE] & PERF_MASK_ADAPTIVE_CALIB)
  }

  async setAdaptiveCalibration(enabled: boolean): Promise<void> {
    await this.setPerfMaskBit(PERF_MASK_ADAPTIVE_CALIB, enabled)
  }

  /** 系统模式（字节1 Bit1）：true=Mac 模式，false=Win 模式。 */
  async getMacMode(): Promise<boolean> {
    return Boolean((await this.readFunc())[FUNC_MAC_MODE_BYTE] & MAC_MODE_MASK)
  }

  async setMacMode(enabled: boolean): Promise<void> {
    await this.mutateFunc((data) => {
      data[FUNC_MAC_MODE_BYTE] = enabled
        ? data[FUNC_MAC_MODE_BYTE] | MAC_MODE_MASK
        : data[FUNC_MAC_MODE_BYTE] & ~MAC_MODE_MASK
    })
  }

  async getFullKeyRollover(): Promise<boolean> {
    return Boolean((await this.readFunc())[FUNC_FULL_KEY_ROLLOVER_BYTE])
  }

  async setFullKeyRollover(enabled: boolean): Promise<void> {
    await this.mutateFunc((data) => {
      data[FUNC_FULL_KEY_ROLLOVER_BYTE] = enabled ? 1 : 0
    })
  }

  /** 连接模式（字节36）：0=2.4G, 1-3=蓝牙1-3通道, 0x0f=有线。 */
  async getConnectionMode(): Promise<GcomeApConnectionMode> {
    const code = (await this.readFunc())[FUNC_CONNECTION_MODE_BYTE]
    if (!(code in GCOME_AP_CONNECTION_MODE_LABELS)) {
      throw new DeviceError(DeviceErrorCode.INVALID_PARAM, `未知连接模式: ${code}`)
    }
    return code as GcomeApConnectionMode
  }

  /** WASD 与方向键交换（改键层面实现，见 keyboard-keys.ts swapWasdKeys）。 */
  async getSwapWasd(): Promise<boolean> {
    const keys = this.device.features.keyboardKeys as GcomeApKeyboardKeys | undefined
    if (!keys) {
      throw new DeviceError(DeviceErrorCode.NOT_SUPPORTED, '当前设备未挂载 keyboardKeys feature')
    }
    return keys.isSwapWasdActive()
  }

  /**
   * WASD 与方向键互换（改键层面实现，见 keyboard-keys.ts swapWasdKeys）。
   * @returns 写后读回确认的互换报告（诊断用，含 target/applied/mismatches）。
   */
  async setSwapWasd(enabled: boolean): Promise<WasdSwapReport> {
    const keys = this.device.features.keyboardKeys as GcomeApKeyboardKeys | undefined
    if (!keys) {
      throw new DeviceError(DeviceErrorCode.NOT_SUPPORTED, '当前设备未挂载 keyboardKeys feature')
    }
    return keys.swapWasdKeys(enabled)
  }

  private async setLockMaskBit(bit: number, enabled: boolean): Promise<void> {
    await this.mutateFunc((data) => {
      const byte = data[FUNC_LOCK_MASK_BYTE]
      data[FUNC_LOCK_MASK_BYTE] = enabled ? byte | bit : byte & ~bit
    })
  }

  private async setPerfMaskBit(bit: number, enabled: boolean): Promise<void> {
    await this.mutateFunc((data) => {
      const byte = data[FUNC_PERF_MASK_BYTE]
      data[FUNC_PERF_MASK_BYTE] = enabled ? byte | bit : byte & ~bit
    })
  }
}
